import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import JsonLd from "@/components/base/JsonLd";
import PageHeader from "@/components/feature/PageHeader";
import ScoopCard from "@/components/feature/ScoopCard";
import useSeo from "@/hooks/useSeo";
import { scoops } from "@/mocks/scoops";
import { categories } from "@/mocks/categories";
import ScoopToolbar from "@/pages/scoops/components/ScoopToolbar";
import Pagination from "@/pages/scoops/components/Pagination";

const ORIGIN = "https://of659.cn";
const PAGE_SIZE = 6;

export default function ScoopsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get("cat") ?? "all";
  const keyword = searchParams.get("q") ?? "";
  const [page, setPage] = useState(1);

  const activeCategory = categories.find((c) => c.key === activeCat);

  useSeo({
    title: activeCategory
      ? `${activeCategory.label}爆料 - 看料爆料线索库｜匿名投稿与核实内容`
      : "爆料库 - 看料爆料｜最新爆料线索、内幕核实与投稿内容",
    description: activeCategory
      ? `浏览${activeCategory.label}分类下的爆料线索，包含投稿人提供的素材与核实状态标注。${activeCategory.desc}`
      : "浏览看料爆料线索库，按明星娱乐、行业内幕、消费维权、社会热点、财经观察、科技数码等分类查看经筛选与核实的爆料内容。",
    keywords: `${activeCategory ? activeCategory.label : "爆料库"},看料爆料,看料爆料投稿,匿名爆料线索,内幕爆料`,
    canonical: `${ORIGIN}/scoops${activeCat !== "all" ? `?cat=${activeCat}` : ""}`,
  });

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return scoops.filter((s) => {
      const matchCat = activeCat === "all" || s.category === activeCat;
      const matchKw =
        !kw ||
        s.title.toLowerCase().includes(kw) ||
        s.excerpt.toLowerCase().includes(kw) ||
        s.tags.some((t) => t.toLowerCase().includes(kw));
      return matchCat && matchKw;
    });
  }, [activeCat, keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
    setPage(1);
  };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: activeCategory ? `${activeCategory.label}爆料线索` : "爆料库",
      url: `${ORIGIN}/scoops${activeCat !== "all" ? `?cat=${activeCat}` : ""}`,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "爆料库", item: `${ORIGIN}/scoops` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: pageItems.map((item, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: item.title,
        url: `${ORIGIN}/scoops/${item.id}`,
      })),
    },
  ];

  return (
    <>
      <JsonLd data={jsonLd} />
      <PageHeader
        crumbs={[{ label: "首页", to: "/" }, { label: "爆料库", to: "/scoops" }]}
        eyebrow="爆料库"
        title={activeCategory ? `${activeCategory.label} · 爆料线索` : "全部爆料线索"}
        desc={
          activeCategory
            ? activeCategory.desc
            : "这里汇总了平台收到并进入核实流程的爆料线索。每条内容都标注了核实状态、来源范围与所在地域，方便你判断信息的可信程度。"
        }
      />

      <section className="mx-auto w-full max-w-shell px-4 py-10 md:px-6 md:py-14">
        <ScoopToolbar
          activeCat={activeCat}
          keyword={keyword}
          resultCount={filtered.length}
          onCatChange={(key) => updateParam("cat", key === "all" ? "" : key)}
          onKeywordChange={(value) => updateParam("q", value)}
        />

        {pageItems.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {pageItems.map((scoop) => (
              <ScoopCard key={scoop.id} scoop={scoop} />
            ))}
          </div>
        ) : (
          <div className="mt-10 rounded-lg border border-dashed border-foreground-950/20 bg-background-100 p-12 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl" aria-hidden="true"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">没有找到匹配的爆料</h2>
            <p className="mt-2 text-sm text-foreground-600">
              换个关键词试试，或者浏览其他分类。如果你手上有相关线索，也欢迎直接投稿。
            </p>
            <Link
              to="/submit"
              className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-mail-send-line text-base" aria-hidden="true"></i>
              我要投稿
            </Link>
          </div>
        )}

        <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
      </section>
    </>
  );
}